import React, { useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  useMediaQuery,
  useTheme,
  CircularProgress,
  Alert,
  Grid,
  Skeleton,
  IconButton,
  SxProps,
} from '@mui/material';
import {
  COLOR_PRIMARY_2,
  COLOR_SUCCESS,
  COLOR_WARNING,
  COLOR_ERROR,
  COLOR_TEXT_1,
  COLOR_TEXT_2,
} from 'src/utils/colors';
import { LockRounded } from '@mui/icons-material';

interface SubmarketInfoTextProps {
  isMobileView: boolean;
  handleOnSampleClick: (plan: string) => void;
  sx?: SxProps;
  submarketSummary: any;
  TopSubmarketGraphComponent: React.ReactNode;
  formattedSubmarketSummary?: any;
}

const DEMO_TEXT =
  'Top submarkets include *Jabal Ali Second, Dubai* and *Al Wasl, Dubai*, both earning the highest neighborhood grades. *Al Muteena, Dubai* is showing upward momentum, while *Al Thanyah  Fourth, Dubai* stands out as a promising area for the luxury segment.';

const SubmarketInfoText = ({
  submarketSummary,
  isMobileView,
  handleOnSampleClick,
  sx,
  TopSubmarketGraphComponent,
  formattedSubmarketSummary,
}: SubmarketInfoTextProps) => {
  const parseLockTextIntoIcon = (text: string, handleOnSampleClick: (plan: string) => void) => {
    const LockIconComponent = (
      <IconButton size="small" sx={{ p: 0 }} onClick={() => handleOnSampleClick('expert')}>
        <LockRounded color="primary" sx={{ fontSize: 'inherit' }} />
      </IconButton>
    );
    if (text.includes('locked')) {
      const boldSplit = text.split('locked');
      const boldTextList = boldSplit.map((text, idx) => (
        <>
          {text} {idx === boldSplit.length - 1 ? <></> : LockIconComponent}
        </>
      ));
      return boldTextList;
    }
    return text;
  };

  const renderWithBold = (
    text: string,
    handleOnSampleClick: (plan: string) => void,
    renderAs: 'text' | 'html' = 'html'
  ) => {
    if (!text) return '';

    const regex = /\*([^*]+)\*/g;
    const parts: (string | JSX.Element)[] = [];
    let lastIndex = 0;
    let match;
    let key = 0;
    while ((match = regex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        parts.push(text.slice(lastIndex, match.index));
      }
      const boldText = match[1] || match[2];
      let formattedBoldText = boldText;

      formattedBoldText =
        renderAs === 'html' ? parseLockTextIntoIcon(boldText, handleOnSampleClick) : boldText;

        if (renderAs === 'text') {
          parts.push(`<b>${formattedBoldText}</b>`);
        } else {
          parts.push(<b key={key++}>{parseLockTextIntoIcon(boldText, handleOnSampleClick)}</b>);
        }
      lastIndex = regex.lastIndex;
    }
    if (lastIndex < text.length) {
      parts.push(text.slice(lastIndex));
    }
    return renderAs === 'text' ? parts.join('') : parts;
  };

  useEffect(() => {
    if(formattedSubmarketSummary ){
      formattedSubmarketSummary.current = renderWithBold(submarketSummary, handleOnSampleClick, 'text');
    }
  }, [submarketSummary]);

  return (
    <Card
      sx={{
        background: '#fff',
        borderRadius: 3,
        boxShadow: '0 2px 12px 0 rgba(16,30,54,.06)',
        border: '1px solid #E0E0E0',
        overflow: 'visible',
        position: 'relative',
        p: 0,
        m: 0,
        minHeight: (isMobileView ? 70 : 100) + 32,
        width: '100%',
        maxWidth: '100vw',
        pb: 0,
        // Top border accent
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '4px',
          background: COLOR_PRIMARY_2,
          borderTopLeftRadius: 8,
          borderTopRightRadius: 8,
          zIndex: 2,
        },
        ...sx,
      }}
    >
      <CardContent sx={{ p: 2, pt: 4, pr: isMobileView ? 2 : 6, pb: '0 !important' }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: isMobileView ? 'flex-start' : 'center',
            gap: 2,
            width: '100%',
          }}
        >
          {/* AI Summary Text */}
          <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
            {/* Market Summary Lines as bullet points */}
            <Box sx={{ pl: 3, mb: 1, mt: 0, listStyle: 'disc', color: COLOR_TEXT_2 }}>
              <Typography variant="body1" sx={{ color: COLOR_TEXT_2 }}>
                {renderWithBold(submarketSummary, handleOnSampleClick)}
              </Typography>
            </Box>
          </Box>

          {TopSubmarketGraphComponent}
        </Box>
      </CardContent>
    </Card>
  );
};

export default SubmarketInfoText;
